import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { JournalCarte } from '../../../utils/types/JournalCarte';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface JournalCarteGridProps {
    articleId: number | null;
    journalCartes: JournalCarte[];
}

const JournalCarteGrid: React.FC<JournalCarteGridProps> = ({ articleId, journalCartes }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<JournalCarte>[]>([
        {
            field: 'code',
            headerName: 'Code',
            sortable: true,
            filter: 'agTextColumnFilter',
        },
        {
            field: 'libelle',
            headerName: 'Libellé',
            sortable: true,
            filter: 'agTextColumnFilter',
        },
        {
            field: 'date',
            headerName: 'Date',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy') : ''),
        },
        {
            field: 'typeOperation',
            headerName: 'Type Opération',
            sortable: true,
            filter: 'agTextColumnFilter',
        },

        {
            field: 'sortie',
            headerName: 'Débit',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';

                if (articleId == null) {
                    return new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(value);
                }
                return `${value.toFixed(2).replace('.', ',')} L`;
            },
            cellStyle: { textAlign: 'right' },
        },

        {
            field: 'entree',
            headerName: 'Crédit',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';

                if (articleId == null) {
                    return new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(value);
                }
                return `${value.toFixed(2).replace('.', ',')} L`;
            },
            cellStyle: { textAlign: 'right' },
        },

        {
            field: 'solde',
            headerName: 'Solde',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';

                if (articleId == null) {
                    return new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(value);
                }
                return `${value.toFixed(2).replace('.', ',')} L`;
            },
            cellStyle: { textAlign: 'right' },
        },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `journal_carte_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: JournalCarte[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const rowObj: Record<string, any> = {};
            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                const header = colDef.headerName ?? field ?? '';
                if (!field) return;

                let value = (row as any)[field];
                if (field === 'date') {
                    value = value ? format(new Date(value), 'dd/MM/yyyy') : '';
                } else if (['entree', 'sortie', 'solde'].includes(field)) {
                    if (articleId == null) {
                        value = new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(value);
                    } else {
                        value = `${value.toFixed(2).replace('.', ',')} L`;
                    }
                }
                rowObj[header] = value;
            });
            return rowObj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'JournalCarte');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `journal_carte_export_${timestamp}.xlsx`);
    }, [articleId]);

    return (
        <>
            <div style={{ height: 500, width: '100%' }}>
                <AgGridReact
                    ref={gridRef}
                    theme={myTheme}
                    getRowId={(params) => params.data.id.toString()}
                    rowData={journalCartes}
                    columnDefs={columnDefs}
                    pagination={true}
                    domLayout="normal"
                    defaultColDef={{
                        resizable: true,
                        editable: false,
                        minWidth: 100,
                        flex: 1,
                    }}
                    suppressExcelExport={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    animateRows={true}
                    overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                />
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default JournalCarteGrid;
